import { useMemo, useState, type FormEvent, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { CircleCheck, Eye, Lock, ShieldCheck, Tags } from 'lucide-react'
import type { TicketDetail } from '@/api/ticketsApi'
import { ApiError } from '@/api/request'
import { Field } from '@/components/common/Field'
import { errorMessage } from '@/components/common/States'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { useResolveTicket, useTicketLookup } from '@/hooks/useTickets'
import {
  DEVIATION_DECISIONS,
  GXP_IMPACTS,
  RESOLUTION_CATEGORIES,
  initialResolveValues,
  normalizeTicketKey,
  serverProblems,
  summaryTemplate,
  validateResolve,
  type ResolveField,
  type ResolveFormValues,
} from '@/lib/resolveForm'
import { viewerTimeZone } from '@/lib/threadView'
import { cn } from '@/lib/utils'

interface ResolveDialogProps {
  ticket: TicketDetail
  routeId: string
  open: boolean
  onOpenChange: (open: boolean) => void
}

/** How each GxP answer reads: the tone of its card. */
const GXP_TONE: Record<string, string> = {
  none: 'data-[on=true]:border-primary data-[on=true]:bg-accent',
  assessed_no_risk: 'data-[on=true]:border-sla-warning data-[on=true]:bg-status-customer-bg',
  yes: 'data-[on=true]:border-status-escalated-fg data-[on=true]:bg-status-escalated-bg',
}

/** A titled step of the form, with its icon and an optional note on the right. */
function Section({ icon: Icon, title, note, children }: { icon: typeof Eye; title: string; note?: ReactNode; children: ReactNode }) {
  return (
    <section className="flex flex-col gap-3" aria-label={title}>
      <header className="flex items-center gap-2">
        <span className="flex size-6 items-center justify-center rounded-md bg-surface-subtle text-ink-muted">
          <Icon className="size-3.5" aria-hidden />
        </span>
        <h3 className="text-meta font-semibold text-ink">{title}</h3>
        {note ? <span className="ml-auto text-label text-ink-muted">{note}</span> : null}
      </header>
      <div className="flex flex-col gap-4 pl-8">{children}</div>
    </section>
  )
}

/** One-tap choices (a radio group): chips, or cards with a line of help. */
function Choices<T extends string>({
  label,
  options,
  value,
  onChange,
  error,
  render,
  layout = 'chips',
}: {
  label: string
  options: readonly T[]
  value: string
  onChange: (v: T) => void
  error?: string | null
  render: (v: T) => { title: string; help?: string; tone?: string }
  layout?: 'chips' | 'cards'
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <span className="text-meta font-medium text-ink">{label}</span>
      <div role="radiogroup" aria-label={label} className={cn(layout === 'cards' ? 'grid gap-2 sm:grid-cols-3' : 'flex flex-wrap gap-1.5')}>
        {options.map((o) => {
          const r = render(o)
          const on = value === o
          return (
            <button
              key={o}
              type="button"
              role="radio"
              aria-checked={on}
              data-on={on}
              onClick={() => onChange(o)}
              className={cn(
                'border text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                layout === 'cards'
                  ? cn('flex flex-col gap-0.5 rounded-lg border-line-strong p-3 hover:border-primary/50', r.tone)
                  : cn('rounded-full px-3 py-1.5 text-meta', on ? 'border-primary bg-accent font-medium text-accent-foreground' : 'border-line-strong text-ink-body hover:border-primary/50'),
              )}
            >
              {layout === 'cards' ? (
                <>
                  <span className={cn('text-meta', on ? 'font-semibold text-ink' : 'font-medium text-ink-body')}>{r.title}</span>
                  {r.help ? <span className="text-label text-ink-muted">{r.help}</span> : null}
                </>
              ) : (
                r.title
              )}
            </button>
          )
        })}
      </div>
      {error ? <p className="text-label text-destructive">{error}</p> : null}
    </div>
  )
}

/**
 * Resolve without e-signature (plan §11): summary (sent to the customer),
 * category (Duplicate asks for the ticket number), root cause (defects), GxP
 * impact, deviation decision and an audited reason. The server re-validates and
 * answers 422 `{ problems }`, shown inline.
 */
export function ResolveDialog({ ticket, routeId, open, onOpenChange }: ResolveDialogProps) {
  const { t } = useTranslation()
  const template = useMemo(
    () => summaryTemplate({ cause: t('resolve.templateCause'), fix: t('resolve.templateFix'), prevention: t('resolve.templatePrevention') }),
    [t],
  )
  const [values, setValues] = useState<ResolveFormValues>(() => initialResolveValues(ticket, template))
  const [errors, setErrors] = useState<Partial<Record<ResolveField, string>>>({})
  const [problems, setProblems] = useState<string[] | null>(null)
  const [lookupError, setLookupError] = useState<string | null>(null)
  const [looking, setLooking] = useState(false)
  const resolve = useResolveTicket(routeId)
  const lookup = useTicketLookup()

  const isDefect = ticket.type.toLowerCase() === 'defect'
  const set = (key: ResolveField, value: string) => setValues((v) => ({ ...v, [key]: value }))
  const err = (key: ResolveField) => (errors[key] ? t(errors[key] as string) : null)

  function reset() {
    setValues(initialResolveValues(ticket, template))
    setErrors({})
    setProblems(null)
    setLookupError(null)
    resolve.reset()
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault()
    setProblems(null)
    setLookupError(null)
    const found = validateResolve(values, ticket.type, template)
    setErrors(found)
    if (Object.keys(found).length > 0) return

    let duplicateOfId: string | undefined
    if (values.category === 'duplicate') {
      const key = normalizeTicketKey(values.duplicateOf) as string
      setLooking(true)
      try {
        duplicateOfId = (await lookup(key)).id
      } catch (error) {
        setLookupError(
          error instanceof ApiError && error.status === 404 ? t('resolve.duplicateNotFound', { key }) : errorMessage(error),
        )
        return
      } finally {
        setLooking(false)
      }
    }

    resolve.mutate(
      {
        summary: values.summary.trim(),
        category: values.category,
        rootCause: values.rootCause.trim() || undefined,
        gxpImpact: values.gxpImpact,
        deviationDecision: values.deviationDecision || undefined,
        deviationRef: values.deviationRef.trim() || undefined,
        duplicateOfId,
        reason: values.reason.trim(),
        timeZone: viewerTimeZone(),
      },
      {
        onSuccess: () => {
          onOpenChange(false)
          reset()
        },
        onError: (error) => setProblems(serverProblems(error)),
      },
    )
  }

  const pending = resolve.isPending || looking
  const errorCount = Object.keys(errors).length

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        onOpenChange(o)
        if (!o) reset()
      }}
    >
      <DialogContent className="flex max-h-[90vh] max-w-2xl flex-col gap-0 overflow-hidden p-0">
        <div className="flex items-start gap-3 border-b border-line px-6 py-5">
          <span className="flex size-9 shrink-0 items-center justify-center rounded-full bg-resolved-bg text-resolved-heading">
            <CircleCheck className="size-5" aria-hidden />
          </span>
          <div className="flex min-w-0 flex-col gap-0.5 pr-6">
            <DialogTitle>{t('resolve.title', { key: ticket.key })}</DialogTitle>
            <p className="truncate text-meta text-ink-body">{ticket.subject}</p>
            <DialogDescription className="text-label text-ink-muted">{t('resolve.description')}</DialogDescription>
          </div>
        </div>

        <form id="resolve-form" className="flex min-h-0 grow flex-col gap-7 overflow-y-auto px-6 py-6" onSubmit={(e) => void onSubmit(e)} noValidate>
          <Section icon={Eye} title={t('resolve.sections.customer')} note={t('resolve.summaryHelp')}>
            <Field id="rs-summary" label={t('resolve.summary')} error={err('summary')}>
              <Textarea id="rs-summary" rows={5} value={values.summary} onChange={(e) => set('summary', e.target.value)} />
            </Field>
          </Section>

          <Section icon={Tags} title={t('resolve.sections.classify')}>
            <Choices
              label={t('resolve.category')}
              options={RESOLUTION_CATEGORIES}
              value={values.category}
              onChange={(v) => set('category', v)}
              error={err('category')}
              render={(c) => ({ title: t(`ticket.category.${c}`) })}
            />
            {values.category === 'duplicate' ? (
              <Field id="rs-duplicate" label={t('resolve.duplicateOf')} error={err('duplicateOf') ?? lookupError}>
                <Input
                  id="rs-duplicate"
                  className="max-w-56 font-mono"
                  placeholder={t('resolve.duplicatePlaceholder')}
                  value={values.duplicateOf}
                  onChange={(e) => set('duplicateOf', e.target.value)}
                />
              </Field>
            ) : null}
            <Field
              id="rs-root"
              label={isDefect ? t('resolve.rootCauseRequired') : `${t('resolve.rootCause')} (${t('common.optional')})`}
              error={err('rootCause')}
            >
              <Textarea id="rs-root" rows={2} value={values.rootCause} onChange={(e) => set('rootCause', e.target.value)} />
            </Field>
          </Section>

          <Section icon={ShieldCheck} title={t('resolve.sections.compliance')}>
            <Choices
              label={t('resolve.gxpImpact')}
              layout="cards"
              options={GXP_IMPACTS.filter((g) => g !== 'unassessed')}
              value={values.gxpImpact}
              onChange={(v) => set('gxpImpact', v)}
              error={err('gxpImpact')}
              render={(g) => ({ title: t(`ticket.gxp.${g}`), help: t(`resolve.gxpHelp.${g}`), tone: GXP_TONE[g] })}
            />
            <Choices
              label={`${t('resolve.deviationDecision')} (${t('common.optional')})`}
              options={['', ...DEVIATION_DECISIONS] as const}
              value={values.deviationDecision}
              onChange={(v) => set('deviationDecision', v)}
              render={(d) => ({ title: d ? t(`ticket.deviation.${d}`) : t('common.none') })}
            />
            {values.deviationDecision === 'raised' ? (
              <Field id="rs-devref" label={t('resolve.deviationRef')} error={err('deviationRef')}>
                <Input
                  id="rs-devref"
                  className="max-w-64 font-mono"
                  placeholder={t('escalate.refPlaceholder')}
                  value={values.deviationRef}
                  onChange={(e) => set('deviationRef', e.target.value)}
                />
              </Field>
            ) : null}
          </Section>

          <Section icon={Lock} title={t('resolve.sections.audit')} note={t('resolve.auditNote')}>
            <Field id="rs-reason" label={t('resolve.reason')} error={err('reason')}>
              <Input id="rs-reason" placeholder={t('resolve.reasonPlaceholder')} value={values.reason} onChange={(e) => set('reason', e.target.value)} />
            </Field>
          </Section>
        </form>

        <div className="flex flex-col gap-3 border-t border-line bg-surface-page px-6 py-4">
          {problems ? (
            <div role="alert" className="rounded-md bg-status-escalated-bg px-3 py-2 text-meta text-status-escalated-fg">
              <p className="font-medium">{t('resolve.serverProblems')}</p>
              <ul className="list-disc pl-5">
                {problems.map((p) => (
                  <li key={p}>{p}</li>
                ))}
              </ul>
            </div>
          ) : resolve.error ? (
            <p role="alert" className="rounded-md bg-status-escalated-bg px-3 py-2 text-meta text-status-escalated-fg">
              {t('common.error', { message: errorMessage(resolve.error) })}
            </p>
          ) : null}
          <div className="flex items-center gap-2">
            <span className="text-label text-ink-muted">
              {errorCount > 0 ? <span className="text-destructive">{t('resolve.fixFields', { count: errorCount })}</span> : t('resolve.footerNote')}
            </span>
            <span className="grow" />
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" form="resolve-form" disabled={pending} className="gap-1.5">
              <CircleCheck className="size-4" aria-hidden />
              {pending ? t('resolve.submitting') : t('resolve.submit')}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
